export interface ArticleSection {
  heading?: string
  paragraphs: string[]
}

export interface Article {
  title: string
  subtitle: string
  date: string
  lastUpdated: string
  category: string
  slug: string
  sections: ArticleSection[]
}

export const articles: Record<string, Article> = {
  'how-ai-search-actually-works': {
    title: 'How AI Search Actually Works (And What to Do About It)',
    subtitle:
      'The discovery layer of the internet has changed — here\u2019s what\u2019s actually happening and how to respond',
    date: 'Feb 2026',
    lastUpdated: 'February 17, 2026',
    category: 'Guide',
    slug: 'how-ai-search-actually-works',
    sections: [
      {
        paragraphs: [
          'The discovery layer of the internet has changed. Just a few years back, ranking on page one of search results almost guaranteed attention. Today, that same page can be completely bypassed if an AI system resolves the user\u2019s intent before they ever click. This shift is not just technological. It is behavioral.',
          'The data shows:',
          'Microsoft\u2019s analysis of 1,200+ websites found AI platforms have 3\u201317\u00d7 higher conversion rates than traditional channels.',
          'Adobe reports that as of January 2026, AI-driven traffic generates 32% higher revenue per visit than traditional traffic.',
          'One caveat is that each LLM provider has its own attribution logic for which sources it prefers to cite. That said, the traffic is clearly higher intent and converts more strongly. This is not a future trend. It is already happening. The real question is no longer whether AI search matters.',
          'It is how visibility inside AI systems actually works.',
        ],
      },
      {
        heading: 'How AI Search Actually Works',
        paragraphs: [
          'To optimize for AI discovery, you first need to understand how it differs from traditional search. When someone asks an AI system:',
          '\u201cWhat are the best running shoes for marathon training?\u201d',
          'the model does not search that exact sentence. Instead, it converts the question into an internal semantic representation. Think of it as a compressed intent model that may resemble something like:',
          '\u201cbest marathon running shoes reviews 2026\u201d',
          'That internal representation can correspond to hundreds of real-world phrasing variations. The system then retrieves information based on semantic similarity, not keyword matching.',
          'In practical terms, AI systems prioritize intent meaning over keywords, source credibility over backlinks, direct answers over headlines, and citation usefulness over ranking position.',
          'This is why keyword optimization alone is no longer sufficient. AI systems are not ranking pages. They are selecting evidence.',
        ],
      },
      {
        heading: 'How To Appear in AI Results',
        paragraphs: [
          'The old approach to visibility was volume. Publish more content, target more keywords, expand surface area. That strategy rarely works in AI search. What AI systems reward is not volume. It is resolution. They prefer sources that clearly solve a user\u2019s problem.',
          'Content still matters, but structure now matters more than length. The most consistently cited content follows a pattern:',
          'Problem \u2192 Solution \u2192 Evidence \u2192 Follow-Up Discussion',
          'Studies suggest this format can increase AI citation likelihood by up to 40%.',
          'Practical guidelines:',
          '\u2022 Deliver the direct answer within the first 60 words (summary, overview, TL;DR, etc.)',
          '\u2022 Include supporting statistics every ~200 words (evidence of legitimacy)',
          '\u2022 Use FAQ formatting when appropriate (signals understanding of reader intent)',
          '\u2022 Add structured schema where possible (bot-friendly formatting)',
          'This is not about gaming systems. It is about making information legible to models that prioritize clarity and evidence.',
        ],
      },
      {
        heading: 'Where AI Systems Actually Look',
        paragraphs: [
          'Publishing content is only half the equation. Distribution determines whether AI systems ever see it. Studies show that AI citations often come from places many teams overlook. For example, technical queries frequently pull from forum-style platforms where users explain solutions in detail.',
          'The important nuance is this:',
          'AI systems do not prefer viral content. They prefer definitive content.',
          'In fact, cited sources are often older, stable, and consistently referenced rather than new or trending. Evergreen material signals reliability. That means helpful contributions in niche communities, documentation-style posts, and clear technical explanations can outperform highly produced marketing content.',
        ],
      },
      {
        heading: 'The Strategic Shift Most Teams Miss',
        paragraphs: [
          'Traditional search evolved slowly. Ranking factors changed gradually, and best practices lasted years. AI search does not behave that way.',
          'Each new model release can shift: preferred sources, citation weighting, reasoning patterns, and trust signals.',
          'What worked last quarter may not work this quarter. Because of that, AI visibility is not a one-time optimization task. It is an ongoing measurement problem. You cannot rely on assumptions about where your brand appears. You need a strategy that continuously observes:',
          '\u2022 Which prompts surface your brand',
          '\u2022 How each prompt performs on each model',
          '\u2022 Which competitors replace you',
          '\u2022 Which sources models trust',
          '\u2022 What changed over time',
          'Without measurement, optimization becomes guesswork.',
        ],
      },
      {
        heading: 'The Bottom Line',
        paragraphs: [
          'AI search is not a new channel layered onto search. It is a different discovery system entirely.',
          'Winning visibility is no longer about ranking pages. It is about being selected as evidence.',
          'Teams that treat AI discovery as a measurable surface will learn faster, adapt faster, and compound visibility over time. Everyone else will still be optimizing for clicks that never happen.',
        ],
      },
    ],
  },
  'why-were-building-andoe-differently': {
    title: 'Why We\u2019re Building Andoe Differently',
    subtitle: 'A philosophy for AI visibility beyond the end game',
    date: 'Jan 2026',
    lastUpdated: 'February 10, 2026',
    category: 'Company',
    slug: 'why-were-building-andoe-differently',
    sections: [
      {
        heading: 'A note on what this is',
        paragraphs: [
          'This is not a feature comparison or a competitor teardown.',
          'It\u2019s an explanation of how we see the future and why that shapes how we build Andoe.',
        ],
      },
      {
        heading: 'The future we believe in',
        paragraphs: [
          'AI is becoming the default interface for discovery and decision-making.',
          'When someone researches a product, compares vendors, or evaluates credibility, they increasingly start with AI.',
          'That shift changes the surface area of a business.',
          'If AI systems are mediating decisions, then how they interpret your company directly influences trust, positioning, and revenue.',
          'We believe this layer becomes foundational infrastructure.',
        ],
      },
      {
        heading: 'Augmentation, not automation',
        paragraphs: [
          'Businesses face endless possible actions.',
          'Automating everything doesn\u2019t create intelligence. It creates noise.',
          'We design Andoe to support judgment, not override it.',
          'The Andoe agent continuously analyze data without user prompting, proactively surfaces insights when it\u2019s worth sharing.',
          'It clarifies tradeoffs, highlights gaps, and prioritizes what matters. Users decide what to execute. We call this agentic analytics.',
        ],
      },
      {
        heading: 'Prompts are strategy',
        paragraphs: [
          'Not all prompts matter equally.',
          'Some drive revenue.',
          'Some shape perception.',
          'Some define category leadership.',
          'Most tools treat prompts as isolated queries. We treat them as strategic assets to be grouped, prioritized, and measured across intent clusters.',
          'Visibility is not just about appearing.',
          'It\u2019s about appearing where it matters.',
        ],
      },
      {
        heading: 'AI visibility is the beginning',
        paragraphs: [
          'Many treat AI visibility as the finish line.',
          'We believe it is the foundation.',
          'As AI becomes the default discovery layer, marketing, sales, and product strategy build on top of it.',
          'You cannot build on something you cannot see.',
        ],
      },
      {
        heading: 'Why bootstrapping matters to the product',
        paragraphs: [
          'Andoe is currently bootstrapped.',
          'This is not about limiting speed. Startups require urgency and strong execution. We care deeply about both.',
          'Bootstrapping allows us to define the infrastructure clearly before we join the most competitive marathon.',
          'AI visibility is not a feature category. It is foundational infrastructure.',
          'As we grow, we hope to partner with investors who share that belief.',
        ],
      },
      {
        heading: 'Every business will have an AI profile',
        paragraphs: [
          'Over time, every business will have an AI profile.',
          'Not something manually curated, but a living representation formed by how AI systems interpret its products, content, reputation, and market position.',
          'Today, that layer is largely invisible.',
          'Most companies cannot see how they are described, misrepresented, or positioned across AI systems. Large enterprises may approximate this internally. Most cannot.',
          'Andoe begins with visibility.',
          'Long term, we are building toward infrastructure that makes this AI profile measurable and improvable for every business.',
          'When companies understand how AI sees them, they can compete intentionally.',
        ],
      },
      {
        heading: 'Final thought',
        paragraphs: [
          'We are building Andoe for a future where AI quietly makes most brand related decisions, and businesses that understand that layer operate with clarity instead of guesswork.',
        ],
      },
    ],
  },
  'you-dont-need-paid-ai-blog-tools': {
    title: 'You don\u2019t need paid AI blog tools to write AI-native content',
    subtitle:
      'How to write blog posts designed for AI summarization, extraction, and follow-up questions',
    date: 'Jan 2026',
    lastUpdated: 'February 10, 2026',
    category: 'Guide',
    slug: 'you-dont-need-paid-ai-blog-tools',
    sections: [
      {
        heading: 'TL;DR',
        paragraphs: [
          '\u2022 Most users will discover your content through AI, not by reading it directly.',
          '\u2022 Blog posts should be designed to be summarized accurately and queried reliably.',
          '\u2022 Structure, definitions, and explicit claims matter more than prose.',
          '\u2022 You do not need paid AI blog tools to produce high-performing content.',
          '\u2022 Prompt strategy and insight into AI perception determine content quality.',
          '\u2022 The goal is to create reference documents, not linear reading experiences.',
        ],
      },
      {
        heading: 'The new reading behavior',
        paragraphs: [
          'Old behavior (human browsing): Search and click a page \u2192 Skim headings \u2192 Read selected paragraphs \u2192 Decide or convert.',
          'New behavior (AI-mediated): Ask AI a question \u2192 AI summarizes multiple sources \u2192 User asks follow-up questions \u2192 AI cites or paraphrases details \u2192 User decides.',
          'Implication: Your content must survive summarization before it is ever read.',
        ],
      },
      {
        heading: 'The goal: make your article summarization-proof',
        paragraphs: [
          'A summarization-proof article has three properties:',
          'Extractable \u2014 Key ideas are clearly stated in places AI systems prioritize.',
          'Stable \u2014 Summaries do not distort the original meaning.',
          'Queryable \u2014 Follow-up questions can be answered without hallucination.',
          'This reframes a blog post as a knowledge object, not a narrative.',
        ],
      },
      {
        heading: 'How AI agents typically scrape blog content',
        paragraphs: [
          'Most AI systems behave predictably:',
          '\u2022 They prioritize the top of the page',
          '\u2022 They chunk content by headings',
          '\u2022 They extract declarative sentences',
          '\u2022 They compress repeated structures',
          'Your formatting should reflect this behavior.',
        ],
      },
      {
        heading: 'The ideal structure for an AI-first blog post',
        paragraphs: [
          '1) Title aligned with real questions \u2014 Avoid cleverness. Prefer clarity.',
          '2) \u201cLast updated\u201d line \u2014 Signals freshness to humans and systems.',
          '3) TL;DR (5\u20138 complete bullets) \u2014 This is the most frequently extracted block.',
          '4) Definitions section \u2014 Anchors meaning and prevents misinterpretation.',
          '5) Modular core sections \u2014 Each section should stand alone. Recommended patterns: What it is, What changes, Why it matters, Implications, What to do.',
          '6) Key takeaways (repetition with compression) \u2014 Confirms the article\u2019s intent for summarizers.',
          '7) FAQ \u2014 Pre-answers follow-up questions users ask AI.',
          '8) Checklist \u2014 Highly reusable and easy to quote.',
        ],
      },
      {
        heading: 'Writing style rules that improve AI summarization',
        paragraphs: [
          '\u2022 One idea per paragraph',
          '\u2022 Declarative statements over rhetoric',
          '\u2022 Explicit nouns instead of pronouns',
          '\u2022 Avoid vague intensifiers',
          '\u2022 Short sentences for key claims',
          '\u2022 Separate claims, examples, and implications',
        ],
      },
      {
        heading: 'Formatting rules that improve extraction',
        paragraphs: [
          '\u2022 One H1 only',
          '\u2022 Consistent H2/H3 hierarchy',
          '\u2022 Parallel grammar in lists',
          '\u2022 Simple tables only when necessary',
          '\u2022 Minimal stylistic formatting',
        ],
      },
      {
        heading: 'Designing for the \u201csummary \u2192 follow-up\u201d loop',
        paragraphs: [
          'Users rarely stop at summaries.',
          'They ask: What does this mean for me? How do I do this? Does this apply to my industry?',
          'Design sections explicitly for those questions.',
          'Use headings that mirror follow-up prompts, such as: \u201cWhat small teams should do first,\u201d \u201cHow to measure this,\u201d \u201cWhat changes in regulated industries.\u201d',
        ],
      },
      {
        heading:
          'You don\u2019t need paid \u201cAI blog tools\u201d to write AI-native content',
        paragraphs: [
          'High-performing AI-native blog content does not require paid writing tools.',
          'General-purpose LLMs are sufficient when paired with strong prompt structure.',
          'Writing for AI means designing for summarization and follow-up questions.',
          'Prompt techniques determine content quality more than software.',
          'Insight into AI perception determines what topics are worth writing about.',
        ],
      },
      {
        heading: 'Why generic blog tools underperform',
        paragraphs: [
          '1) They optimize for output, not understanding \u2014 They generate volume without stable meaning.',
          '2) They assume human reading patterns \u2014 They optimize for scrolling, not retrieval.',
          '3) They collapse strategy into execution \u2014 They skip deciding what is worth saying.',
        ],
      },
      {
        heading: 'The real leverage: prompt strategy',
        paragraphs: [
          'Modern LLMs are already capable. The difference is prompting.',
          'Technique 1: Force structure before content \u2014 \u201cCreate an outline for a blog post designed to be summarized accurately by AI systems. Include TL;DR, definitions, numbered sections, checklist, and FAQ.\u201d',
          'Technique 2: Explicitly design for summarization \u2014 \u201cWrite this assuming readers will ask an AI to summarize it before reading.\u201d',
          'Technique 3: One-idea-per-section constraint \u2014 \u201cEach section should cover exactly one idea using short, declarative paragraphs.\u201d',
          'Technique 4: Add definitions proactively \u2014 \u201cInclude a definitions section explaining key terms in plain language.\u201d',
          'Technique 5: Separate claims, implications, and actions \u2014 \u201cFor each major idea, include: what it is, why it matters, implication, and action.\u201d',
          'Technique 6: Pre-write follow-up questions \u2014 \u201cAdd an FAQ answering the most likely questions after a summary.\u201d',
          'Technique 7: Ask the model to self-evaluate \u2014 \u201cRate this article on AI agent scrape-ability and suggest improvements.\u201d',
        ],
      },
      {
        heading: 'Where Andoe fits (without the pitch)',
        paragraphs: [
          'Even with strong prompting, one question remains difficult: What should we write about at all?',
          'Andoe\u2019s Insights feature helps teams:',
          '\u2022 Observe how AI systems describe their category',
          '\u2022 See which questions AI consistently answers',
          '\u2022 Identify gaps between AI perception and intended positioning',
          '\u2022 Design prompt strategies grounded in real AI responses',
          'This does not automate writing. It informs what is worth writing.',
        ],
      },
      {
        heading: 'Practical workflow (no paid blog tool required)',
        paragraphs: [
          '1. Use Andoe Insights to identify relevant intents and narratives',
          '2. Define the core claim manually',
          '3. Use a general LLM with structured prompts',
          '4. Write modular sections',
          '5. Add definitions, checklist, and FAQ',
          '6. Self-score scrape-ability before publishing',
        ],
      },
      {
        heading: 'Key takeaways',
        paragraphs: [
          '\u2022 You do not need specialized blog tools.',
          '\u2022 Prompt strategy matters more than software.',
          '\u2022 Structure beats style.',
          '\u2022 AI-first writing prioritizes summarization and retrieval.',
          '\u2022 Insight into AI perception determines content relevance.',
        ],
      },
      {
        heading: 'Final thought',
        paragraphs: [
          'Content is no longer read first.',
          'It is summarized first, interrogated second, and trusted last.',
          'Design for that order, and distribution follows.',
        ],
      },
    ],
  },
  'when-discovery-lives-inside-ai': {
    title: 'When Discovery Lives Inside AI: What Happens to Businesses Next?',
    subtitle: '',
    date: 'Feb 2026',
    lastUpdated: 'February 10, 2026',
    category: 'Perspective',
    slug: 'when-discovery-lives-inside-ai',
    sections: [
      {
        heading: 'TL;DR',
        paragraphs: [
          '\u2022 Discovery is shifting from humans browsing to AI systems summarizing and recommending.',
          '\u2022 Visibility is no longer about traffic or rankings but about whether AI models recognize and trust your business.',
          '\u2022 AI compresses markets, resulting in fewer winners per category.',
          '\u2022 Brand consistency becomes a prerequisite for AI inclusion, not a marketing layer.',
          '\u2022 Strategy matters more than execution as AI makes output cheap.',
          '\u2022 Most businesses risk silent disappearance if they do not monitor how AI understands them.',
        ],
      },
      {
        heading: 'Definitions',
        paragraphs: [
          'AI discovery \u2014 The process by which users rely on AI systems (e.g., AI overviews, chat-based models) to research, evaluate, and decide, instead of browsing multiple sources directly.',
          'AI visibility \u2014 How often and how favorably AI systems mention, describe, or recommend a business for relevant intents.',
          'Model recognition \u2014 Whether an AI system correctly identifies what a business is, who it is for, and when it is relevant.',
          'Narrative consistency \u2014 The degree to which a business presents the same category definition, positioning, and value proposition across public sources.',
        ],
      },
      {
        heading: 'Core premise',
        paragraphs: [
          'If discovery moves inside AI systems, markets stop being navigated by people and start being navigated by models.',
          'This changes: how visibility works, how competition works, and how businesses grow or disappear.',
        ],
      },
      {
        heading: '1. Discovery becomes model-mediated',
        paragraphs: [
          'In an AI-first world, users do not explore options manually.',
          'What changes \u2014 Users ask one question instead of browsing multiple sources. AI systems interpret intent and generate a synthesized answer. The long tail of options is hidden from the user.',
          'Why this matters \u2014 AI systems decide which businesses are worth mentioning before the user ever sees an option.',
          'Implication \u2014 If your business is not included in the model\u2019s response, it does not participate in discovery.',
        ],
      },
      {
        heading: '2. Visibility shifts from traffic to recognition',
        paragraphs: [
          'Traditional visibility depended on ranking and clicks. AI visibility depends on understanding and trust.',
          'What AI systems evaluate \u2014 Category clarity: what type of business you are. Relevance: when you are the right answer. Trust: whether your information is reliable.',
          'Why this matters \u2014 A business can have strong SEO metrics and still be invisible to AI systems.',
          'Implication \u2014 Visibility becomes binary faster. There is no \u201cpage two\u201d in AI answers.',
        ],
      },
      {
        heading: '3. Markets concentrate into fewer winners',
        paragraphs: [
          'AI systems reduce choice by design.',
          'What changes \u2014 AI often recommends one option or a short list. Alternatives outside the shortlist are rarely explored. Category explanations influence which features matter.',
          'Why this matters \u2014 Demand concentrates more aggressively than in traditional search.',
          'Implication \u2014 Being \u201csecond best\u201d often results in zero demand.',
        ],
      },
      {
        heading: '4. Brand becomes infrastructure',
        paragraphs: [
          'Brand is no longer presentation. It becomes input data.',
          'How AI uses brand signals \u2014 Repeated mentions across credible sources. Stable positioning language. Clear category definitions.',
          'Why this matters \u2014 Inconsistent branding leads to misclassification or hallucinated summaries.',
          'Implications \u2014 Large companies lose advantage if their narrative is fragmented. Small companies gain leverage if their narrative is precise.',
        ],
      },
      {
        heading: '5. Strategy separates from execution',
        paragraphs: [
          'AI dramatically reduces the cost of producing output.',
          'What becomes cheap \u2014 Content creation. Campaign execution. Variations and experiments.',
          'What stays expensive \u2014 Framing. Positioning. Editorial judgment.',
          'Implication \u2014 Businesses that confuse activity with strategy will produce more and matter less.',
        ],
      },
      {
        heading: '6. Measurement shifts from performance to perception',
        paragraphs: [
          'Traditional analytics focus on human behavior. AI discovery requires different signals.',
          'What stops working \u2014 Click-through rates as primary indicators. Referral traffic as the sole visibility signal.',
          'What becomes critical \u2014 Whether AI mentions your business. How AI frames your value. Which competitors AI groups you with.',
          'Implication \u2014 Businesses can lose AI visibility without noticing immediate revenue impact.',
        ],
      },
      {
        heading: '7. The risk of silent disappearance',
        paragraphs: [
          'AI-driven change does not feel disruptive at first.',
          'What happens \u2014 Existing channels mask early decline. AI defaults form gradually. Perception hardens before humans notice.',
          'Implication \u2014 By the time demand drops, the model\u2019s understanding is already entrenched.',
        ],
      },
      {
        heading: 'Key takeaways (for humans and AI agents)',
        paragraphs: [
          '\u2022 AI discovery compresses markets and concentrates demand.',
          '\u2022 Visibility depends on how AI understands and trusts a business.',
          '\u2022 Brand consistency is a prerequisite for AI inclusion.',
          '\u2022 Strategy compounds; execution commoditizes.',
          '\u2022 The biggest risk is being omitted, not criticized.',
        ],
      },
      {
        heading: 'Practical checklist',
        paragraphs: [
          '\u2022 Define your category in one sentence.',
          '\u2022 Define your ideal customer and intent clearly.',
          '\u2022 Audit how AI currently describes your business.',
          '\u2022 Align homepage, pricing, and about-page language.',
          '\u2022 Ensure consistent mentions across authoritative sources.',
          '\u2022 Monitor AI responses regularly, not quarterly.',
          '\u2022 Treat AI visibility as an ongoing system, not a campaign.',
        ],
      },
      {
        heading: 'Final thought',
        paragraphs: [
          'AI is not replacing discovery.',
          'It is replacing who does the discovering.',
          'In that world, the businesses that win are not the loudest.',
          'They are the clearest.',
        ],
      },
    ],
  },
  'how-to-think-about-prompts-in-an-ai-first-world': {
    title: 'How to Think About Prompts in an AI-First World',
    subtitle: 'From keywords and clicks to intent and understanding',
    date: 'Feb 2026',
    lastUpdated: 'February 10, 2026',
    category: 'Perspective',
    slug: 'how-to-think-about-prompts-in-an-ai-first-world',
    sections: [
      {
        paragraphs: [
          'For a long time, discovery followed a simple logic.',
          'People searched.',
          'They clicked links.',
          'Success was measured in traffic.',
          'That model shaped how we wrote content, how we chose keywords, and how we thought about visibility. If you could attract the click, you won.',
          'That mental model is quietly breaking.',
          'As discovery moves into AI systems, people are no longer navigating pages. They are asking questions and accepting synthesized answers. The goal is no longer to earn attention, but to resolve intent.',
          'This shift changes how prompts should be understood.',
        ],
      },
      {
        heading: 'Prompts are not commands. They are expressions of intent.',
        paragraphs: [
          'A prompt is often treated like an instruction. Something to optimize, refine, or phrase precisely.',
          'In reality, a prompt is not a command to a system. It is an expression of human intent. It reflects what someone is trying to decide, not how well they know how to ask.',
          'When people interact with AI, they are rarely trying to be exact. They are trying to move forward. They are trying to understand what to trust, what makes sense for their situation, or what someone like them would choose next.',
          'The wording is surface-level.',
          'The intent underneath is the signal.',
          'AI systems are designed to infer that signal. Businesses need to do the same.',
        ],
      },
      {
        heading: 'We are moving from a click economy to an intent economy.',
        paragraphs: [
          'In the click economy, visibility meant attention. The job was to pull someone onto a page. Ambiguity often helped because curiosity drove clicks.',
          'In an intent economy, visibility means being the right answer. AI systems are not optimizing for attraction. They are optimizing for resolution.',
          'They attempt to understand what the user is actually trying to accomplish, what constraints matter, and what tradeoffs are implied. The output is not a list of options, but a framed recommendation.',
          'This is why prompts are no longer entry points to content. They are entry points to decisions.',
          'And first explanations matter more than ever.',
        ],
      },
      {
        heading:
          '\u201cGeneral vs specific\u201d is the wrong way to think about prompts.',
        paragraphs: [
          'A common question teams ask is whether prompts should be broad or narrow.',
          'That question misses the point.',
          'What matters is decision proximity, meaning how close a person is to acting when they ask a question.',
          'When someone is early in their thinking, prompts tend to be broad. They are orienting themselves, trying to understand a space or learn what matters. These prompts are about context, not commitment.',
          'As people move closer to a decision, prompts become more specific. Risk increases. Comparisons sharpen. The user is no longer exploring what exists, but deciding what to choose.',
          'Neither type of prompt is better. They simply reflect different moments in a decision journey.',
          'The strategy is not choosing one.',
          'The strategy is understanding both.',
        ],
      },
      {
        heading: 'The real mistake is treating all prompts as equal.',
        paragraphs: [
          'Just as not all keywords were equal in the search era, not all prompts matter the same in an AI-first world.',
          'Some prompts shape how a category is understood. Others influence trust at the moment of choice. Many exist purely out of curiosity and never lead to action.',
          'When teams treat prompts as a flat list, they repeat the same mistake they once made with keywords. Volume replaces judgment.',
          'The strategic work is not collecting prompts.',
          'It is deciding which intents actually matter.',
        ],
      },
      {
        heading: 'Prompts should be organized by intent, not wording.',
        paragraphs: [
          'Two prompts can look different and mean the same thing.',
          'AI systems are already good at collapsing phrasing into meaning. Humans should follow that lead.',
          'The opportunity is not optimizing how a question is worded. It is recognizing what decision the question represents, what uncertainty it reflects, and what narrative it reinforces.',
          'When prompts are grouped by intent rather than phrasing, patterns emerge. You start to see where AI consistently frames your category in a certain way, where trust breaks down, and where differentiation actually matters.',
          'This is where prompt strategy becomes real strategy.',
        ],
      },
      {
        heading: 'Specificity is not chosen. It emerges.',
        paragraphs: [
          'Teams often feel pressure to decide whether they should \u201cstart broad\u201d or \u201cgo niche.\u201d',
          'In practice, specificity emerges naturally as understanding deepens.',
          'Broad prompts reveal how AI systems currently frame the category. They show defaults, assumptions, and common language. Specific prompts reveal where those assumptions fail, where nuance matters, and where decisions flip.',
          'You don\u2019t commit to one or the other. You move between them as intent sharpens.',
          'The mistake is locking yourself into one level of abstraction.',
        ],
      },
      {
        heading: 'Prompts are a strategy layer, not a task list.',
        paragraphs: [
          'The biggest shift is mental.',
          'Prompts are no longer things to check off. They are representations of real decision moments. Each meaningful prompt reflects a question a customer is asking before they choose, trust, or walk away.',
          'When you see prompts this way, the work changes.',
          'You stop asking how many prompts you track.',
          'You start asking which decisions you want to influence.',
          'That is the difference between measurement and strategy.',
        ],
      },
      {
        heading: 'Why this matters more as AI adoption grows.',
        paragraphs: [
          'As AI becomes a default interface for discovery, fewer people will browse widely. They will ask once, trust the synthesis, and move forward.',
          'That concentrates power at the moment of explanation.',
          'Prompts shape what gets explained first. And first explanations tend to stick.',
          'This is why prompt strategy matters more as AI adoption increases, not less. It determines how your business is framed before anyone ever visits your site.',
        ],
      },
      {
        heading: 'A better way to think about prompt strategy.',
        paragraphs: [
          'Instead of debating general versus specific prompts, ask simpler questions.',
          'What decision is this prompt attached to?',
          'How close is the user to acting?',
          'What do they already assume?',
          'What would change their mind?',
          'If you can answer those, the wording almost doesn\u2019t matter. AI systems will handle phrasing. Your responsibility is understanding intent.',
        ],
      },
      {
        heading: 'Final thought',
        paragraphs: [
          'The click economy rewarded attention.',
          'The intent economy rewards understanding.',
          'Prompts are no longer about traffic. They are about presence at the moment someone decides.',
          'The companies that win won\u2019t be the ones tracking the most prompts. They\u2019ll be the ones who understand which questions truly matter, and why.',
        ],
      },
    ],
  },
}

// Ordered list for the blog listing page (newest first)
export const articleList = [
  articles['how-ai-search-actually-works'],
  articles['how-to-think-about-prompts-in-an-ai-first-world'],
  articles['why-were-building-andoe-differently'],
]
